///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface EarthquakeProperties extends DatasetFeature {
	id: string
	place: string
	title: string
	lon: number
	lat: number
	depth_km: number
	mag: number
	mag_type: string | null
	type: string
	event_time: string
	updated: string
	ingested_at: string

	dmin: number | null
	rms: number | null
	gap: number | null

	felt: number | null
	cdi: number | null
	mmi: number | null
	alert: string | null
	tsunami: number

	sig: number | null
	status: string

	net: string
	code: string
	ids: string
	sources: string
	types: string
	nst: number | null

	url: string
	detail: string
}

const cBatchSize = 500

export class EarthquakesTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists earthquakes (
					id text primary key,
					place text not null,
					title text not null,
					lon real not null,
					lat real not null,
					depth_km real not null,
					mag real not null,
					mag_type text,
					type text not null,
					event_time text not null,
					updated text not null,
					ingested_at text not null,

					dmin real,
					rms real,
					gap real,

					felt integer,
					cdi real,
					mmi real,
					alert text,
					tsunami integer,

					sig integer,
					status text not null,

					net text not null,
					code text not null,
					ids text not null,
					sources text not null,
					types text not null,
					nst integer,

					url text not null,
					detail text not null,

					geom blob
				)`
			},
			{ sql: "create index if not exists idx_earthquakes_event_time on earthquakes(event_time desc)" },
			{ sql: "create index if not exists idx_earthquakes_updated on earthquakes(updated desc)" },
			{ sql: "create index if not exists idx_earthquakes_mag on earthquakes(mag desc)" },
			{ sql: "create index if not exists idx_earthquakes_depth on earthquakes(depth_km asc)" },
			{ sql: "create index if not exists idx_earthquakes_alert on earthquakes(alert)" },
			{ sql: "create index if not exists idx_earthquakes_tsunami on earthquakes(tsunami)" },

			{
				sql: `create virtual table if not exists earthquakes_idx using rtree(
					id,
					min_x, max_x,
					min_y, max_y
				)`
			},
			{
				sql: `create trigger if not exists earthquakes_idx_insert
				after insert on earthquakes
				begin
					insert into earthquakes_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists earthquakes_idx_update
				after update on earthquakes
				begin
					delete from earthquakes_idx where id = old.rowid;
					insert into earthquakes_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists earthquakes_idx_delete
				after delete on earthquakes
				begin
					delete from earthquakes_idx where id = old.rowid;
				end`
			},
		])
	}

	public static async replaceAll(rows: EarthquakeProperties[]): Promise<void> {
		await Database.db.exec("delete from earthquakes", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: EarthquakeProperties[]): Promise<void> {
		const statement = `insert into earthquakes (
				id, place, title, lon, lat, depth_km, mag, mag_type, type, event_time, updated, ingested_at,
				dmin, rms, gap,
				felt, cdi, mmi, alert, tsunami,
				sig, status,
				net, code, ids, sources, types, nst,
				url, detail,
				geom
			) values (
				?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
				?, ?, ?,
				?, ?, ?, ?, ?,
				?, ?,
				?, ?, ?, ?, ?, ?,
				?, ?,
				ST_SetSRID(ST_MakePoint(?, ?, ?), 4326)
			)
			on conflict(id) do update set
				place = excluded.place,
				title = excluded.title,
				lon = excluded.lon,
				lat = excluded.lat,
				depth_km = excluded.depth_km,
				mag = excluded.mag,
				mag_type = excluded.mag_type,
				type = excluded.type,
				event_time = excluded.event_time,
				updated = excluded.updated,
				ingested_at = excluded.ingested_at,
				dmin = excluded.dmin,
				rms = excluded.rms,
				gap = excluded.gap,
				felt = excluded.felt,
				cdi = excluded.cdi,
				mmi = excluded.mmi,
				alert = excluded.alert,
				tsunami = excluded.tsunami,
				sig = excluded.sig,
				status = excluded.status,
				net = excluded.net,
				code = excluded.code,
				ids = excluded.ids,
				sources = excluded.sources,
				types = excluded.types,
				nst = excluded.nst,
				url = excluded.url,
				detail = excluded.detail,
				geom = excluded.geom`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(q => [
				q.id,
				q.place,
				q.title,
				q.lon,
				q.lat,
				q.depth_km,
				q.mag,
				q.mag_type,
				q.type,
				q.event_time,
				q.updated,
				q.ingested_at,
				q.dmin,
				q.rms,
				q.gap,
				q.felt,
				q.cdi,
				q.mmi,
				q.alert,
				q.tsunami,
				q.sig,
				q.status,
				q.net,
				q.code,
				q.ids,
				q.sources,
				q.types,
				q.nst,
				q.url,
				q.detail,
				q.lon,
				q.lat,
				(q.depth_km ?? 0) * 1000,
			]))
		}
	}

	public static async deleteByIds(ids: string[]): Promise<void> {
		if (ids.length === 0) return
		const placeholders = ids.map(() => "?").join(",")
		await Database.db.exec(`delete from earthquakes where id in (${placeholders})`, ids, "critical")
	}

	public static async getAll(): Promise<EarthquakeProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, place, title, lon, lat, depth_km, mag, mag_type, type, event_time, updated, ingested_at,
				dmin, rms, gap,
				felt, cdi, mmi, alert, tsunami,
				sig, status,
				net, code, ids, sources, types, nst,
				url, detail
			from earthquakes
			order by event_time desc`,
			[],
			"low"
		)
		return rows as EarthquakeProperties[]
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from earthquakes`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	public static async getById(id: string): Promise<EarthquakeProperties | null> {
		const rows = await Database.db.exec(
			`select
				id, place, title, lon, lat, depth_km, mag, mag_type, type, event_time, updated, ingested_at,
				dmin, rms, gap,
				felt, cdi, mmi, alert, tsunami,
				sig, status,
				net, code, ids, sources, types, nst,
				url, detail
			from earthquakes
			where id = ?
			limit 1`,
			[id],
			"high"
		) as EarthquakeProperties[]
		return rows[0] ?? null
	}

	public static async getLatest(): Promise<EarthquakeProperties | null> {
		const rows = await Database.db.exec(
			`select
				id, place, title, lon, lat, depth_km, mag, mag_type, type, event_time, updated, ingested_at,
				dmin, rms, gap,
				felt, cdi, mmi, alert, tsunami,
				sig, status,
				net, code, ids, sources, types, nst,
				url, detail
			from earthquakes
			order by event_time desc
			limit 1`,
			[],
			"low"
		) as EarthquakeProperties[]
		return rows[0] ?? null
	}
}


